import React, { useEffect, useState } from "react";

import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/uiComponents/components/ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/uiComponents/components/ui/chart";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { getAllExpensesApi } from "@/modules/organizationServices";

import { allExpenses } from "@/store/redux/organization/graphExpenses";
import { Button } from "../ui/button";
import { DateList } from "@/enums/dateList";
import { applyExpenseFilters } from "@/utils/filterHelper";
import { setFilteredExpenses } from "@/store/redux/filterSlice";
import { DateFilter } from "@/types/filter";
import { chartHelper } from "@/utils/chartHelper";

const chartConfig = {
  amount: {
    label: "Amount",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

export function ChartAreaInteractive() {
  const dispatch = useDispatch();

  const [clickedButton, setClickedButton] = useState<DateList>(
    DateList.LAST_MONTH
  );
  const expenses = useSelector(
    (state: RootState) => state.graph.expensesForGraph
  );
  const filter = useSelector((state: RootState) => state.filter.filter);
  const [timeFilter, setTimeFilter] = useState<DateFilter | null>(null);

  const filteredExpenses =
    timeFilter !== null ? applyExpenseFilters(expenses, timeFilter) : expenses;
  useEffect(() => {
    dispatch(setFilteredExpenses(filteredExpenses));
  }, [filteredExpenses, dispatch]);

  const getMonthlyTicks = (expenses: { date: string }[]) => {
    const seen = new Set<string>();
    const ticks: string[] = [];

    for (const e of expenses) {
      const [year, month] = e.date.split("-");
      const y = parseInt(year);
      const m = parseInt(month);
      if (!isNaN(y) && !isNaN(m)) {
        const key = `${y}-${m}`;
        if (!seen.has(key)) {
          seen.add(key);
          ticks.push(e.date);
        }
      }
    }

    return ticks;
  };

  const getWeeklyTicks = (expenses: { date: string }[]) => {
    const seen = new Set<string>();
    const ticks: string[] = [];

    for (const e of expenses) {
      const d = new Date(e.date);
      const sunday = new Date(d);
      sunday.setDate(d.getDate() - d.getDay());
      const weekStartStr = sunday.toISOString().split("T")[0];

      if (!seen.has(weekStartStr)) {
        seen.add(weekStartStr);
        ticks.push(weekStartStr);
      }
    }

    return ticks;
  };

  const getExpenses = async () => {
    try {
      const response = await getAllExpensesApi(filter);
      const expense = Array.isArray(response) ? response : [response];
      dispatch(allExpenses(expense));
    } catch (error) {
      console.error("Failed to fetch expenses:", error);
    }
  };

  useEffect(() => {
    getExpenses();
  }, [filter]);

  const formatExpensesData = () => {
    switch (clickedButton) {
      case DateList.LAST_YEAR:
        return chartHelper(365, filteredExpenses);

      case DateList.LAST_SIX_MONTHS:
        return chartHelper(180, filteredExpenses);

      case DateList.LAST_THREE_MONTHS:
        return chartHelper(90, filteredExpenses);

      case DateList.LAST_MONTH:
        return chartHelper(30, filteredExpenses);

      default:
        return filteredExpenses.map((e) => ({
          date: e.date,
          amount: Number(e.amount),
        }));
    }
  };

  const formattedExpenses = formatExpensesData();
  const ticks =
    clickedButton === DateList.LAST_YEAR
      ? getMonthlyTicks(formattedExpenses)
      : [DateList.LAST_SIX_MONTHS, DateList.LAST_THREE_MONTHS].includes(
            clickedButton
          )
        ? getWeeklyTicks(formattedExpenses)
        : undefined;

  const allValues = formattedExpenses.map((d) => d.amount);
  const maxAmount = allValues.length ? Math.max(...allValues) : 0;
  const minAmount = 0;
  const buffer = (maxAmount - minAmount) * 0.1;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Expenses</CardTitle>
        <div className="flex justify-between">
          <CardDescription>Total Expenses</CardDescription>
          <div className="flex flex-wrap justify-end gap-0.5">
            <Button
              variant={"outline"}
              className={`rounded-[3px] cursor-pointer ${clickedButton === DateList.LAST_YEAR ? "bg-[#D6E3E8] hover:bg-[#D6E3E8] " : "bg-[#e9e9e9] hover:bg-[#f3f3f3]"}`}
              onClick={() => {
                setTimeFilter({ date: [DateList.LAST_YEAR] });
                setClickedButton(DateList.LAST_YEAR);
              }}
            >
              {DateList.LAST_YEAR}
            </Button>
            <Button
              variant={"outline"}
              className={`rounded-[3px] cursor-pointer ${clickedButton === DateList.LAST_SIX_MONTHS ? "bg-[#D6E3E8] hover:bg-[#D6E3E8] " : "bg-[#e9e9e9] hover:bg-[#f3f3f3]"}`}
              onClick={() => {
                setTimeFilter({ date: [DateList.LAST_SIX_MONTHS] });
                setClickedButton(DateList.LAST_SIX_MONTHS);
              }}
            >
              {DateList.LAST_SIX_MONTHS}
            </Button>
            <Button
              variant={"outline"}
              className={`rounded-[3px] cursor-pointer ${clickedButton === DateList.LAST_THREE_MONTHS ? "bg-[#D6E3E8] hover:bg-[#D6E3E8] " : "bg-[#e9e9e9] hover:bg-[#f3f3f3]"}`}
              onClick={() => {
                setTimeFilter({ date: [DateList.LAST_THREE_MONTHS] });
                setClickedButton(DateList.LAST_THREE_MONTHS);
              }}
            >
              {DateList.LAST_THREE_MONTHS}
            </Button>
            <Button
              variant={"outline"}
              className={`rounded-[3px] cursor-pointer ${clickedButton === DateList.LAST_MONTH ? "bg-[#D6E3E8] hover:bg-[#D6E3E8] " : "bg-[#e9e9e9] hover:bg-[#f3f3f3]"}`}
              onClick={() => {
                setTimeFilter({ date: [DateList.LAST_MONTH] });
                setClickedButton(DateList.LAST_MONTH);
              }}
            >
              {DateList.LAST_MONTH}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[250px] w-full">
          <LineChart data={formattedExpenses} margin={{ left: 12, right: 12 }}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              ticks={ticks}
              tickFormatter={(value) => {
                const clicked = clickedButton;

                if (clicked === DateList.LAST_YEAR) {
                  const [year, month] = value.split("-");
                  const y = parseInt(year);
                  const m = parseInt(month);
                  if (!isNaN(y) && !isNaN(m)) {
                    const dateObj = new Date(y, m - 1);
                    return dateObj.toLocaleString("default", {
                      month: "short",
                    });
                  }
                  return value;
                }

                if (
                  [
                    DateList.LAST_SIX_MONTHS,
                    DateList.LAST_THREE_MONTHS,
                  ].includes(clicked)
                ) {
                  const startDate = new Date(value);
                  if (isNaN(startDate.getTime())) return "Invalid";

                  const endDate = new Date(startDate);
                  endDate.setDate(startDate.getDate() + 7);

                  const startLabel = startDate.toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  });

                  return `${startLabel} `;
                }

                if (clicked === DateList.LAST_MONTH) {
                  const dateObj = new Date(value);
                  return isNaN(dateObj.getTime())
                    ? "Invalid"
                    : dateObj.toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      });
                }

                const dateObj = new Date(value);
                return isNaN(dateObj.getTime())
                  ? "Invalid"
                  : dateObj.toLocaleDateString();
              }}
            />

            <YAxis
              domain={[0, maxAmount + buffer]}
              padding={{ top: 20, bottom: 10 }}
              label={{
                value: "Amount (Rs.)",
                angle: -90,
                position: "insideLeft",
                offset: 5,
                style: {
                  textAnchor: "middle",
                  fill: "var(--foreground)",
                  fontSize: "14px",
                },
              }}
            />

            <ChartTooltip
              cursor={false}
              content={<ChartTooltipContent hideLabel />}
              formatter={(value) => [`Amount: Rs. ${value}`]}
            />
            <Line
              dataKey="amount"
              type="monotone"
              stroke="#38bdf8"
              strokeWidth={2}
              animationDuration={1000}
              dot={false}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
